import { Share } from '@capacitor/share';
import { Capacitor } from '@capacitor/core';
import { useState } from 'react';
import type { V2Inspection } from '../../domain/inspection';
import { useT, type MessageKey } from '../../i18n/i18n';
import { useStore } from '../../state/createStore';
import { checkRemoteSignAction, createRemoteSignLinkAction, discardRemoteSignAction, remoteSignReady, remoteSignStore, type RemoteCheckResult, type RemoteCreateResult } from '../../state/remoteSign';
import { Button, InlineMessage, SheetShell } from '../../ui/components';
import { formatDate } from '../../ui/hooks';

// 发链接远程签 (Owner decision list 2026-10-19 items 4 / 45): the client is not here, so the inspector sends a
// one-time link. The client reads the whole agreement on their own phone or computer, ticks the two consents,
// types their name and signs; back here the inspector checks, the client's part is taken into the agreement and
// the server's copy deleted, and the inspector signs on this device as usual.
const CREATE_FAILURES: Record<Extract<RemoteCreateResult, { kind: 'failed' }>['reason'], MessageKey> = {
  notReady: 'remote.notReady',
  noWebAddress: 'remote.noWebAddress',
  offline: 'remote.offline',
  notSignedIn: 'remote.notSignedIn',
  unavailable: 'remote.unavailable'
};

const CHECK_MESSAGES: Record<RemoteCheckResult['kind'], { kind: 'ok' | 'err' | 'info'; key: MessageKey }> = {
  waiting: { kind: 'info', key: 'remote.waiting' },
  taken: { kind: 'ok', key: 'remote.taken' },
  expired: { kind: 'err', key: 'remote.expired' },
  changed: { kind: 'err', key: 'remote.changed' },
  gone: { kind: 'err', key: 'remote.gone' },
  failed: { kind: 'err', key: 'remote.unavailable' }
};

export function RemoteSignSheet(props: { inspection: V2Inspection; onClose: () => void; onSignNow: () => void }) {
  const { t, locale } = useT();
  const link = useStore(remoteSignStore, (s) => s.links.find((l) => l.inspectionId === props.inspection.id) ?? null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ kind: 'ok' | 'err' | 'info'; text: string } | null>(null);
  const [taken, setTaken] = useState(false);
  const ready = remoteSignReady(props.inspection.agreement);

  async function create() {
    setBusy(true);
    setMessage(null);
    try {
      const result = await createRemoteSignLinkAction(props.inspection.id, locale);
      if (result.kind === 'failed') setMessage({ kind: 'err', text: t(CREATE_FAILURES[result.reason]) });
    } finally {
      setBusy(false);
    }
  }

  async function check() {
    setBusy(true);
    setMessage(null);
    try {
      const result = await checkRemoteSignAction(props.inspection.id);
      if (result.kind === 'failed') setMessage({ kind: 'err', text: t(result.reason === 'offline' ? 'remote.offline' : 'remote.unavailable') });
      else setMessage({ kind: CHECK_MESSAGES[result.kind].kind, text: t(CHECK_MESSAGES[result.kind].key) });
      if (result.kind === 'taken') setTaken(true);
      if (result.kind === 'changed') await discardRemoteSignAction(props.inspection.id);
    } finally {
      setBusy(false);
    }
  }

  async function copy() {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link.url);
      setMessage({ kind: 'ok', text: t('remote.copied') });
    } catch {
      setMessage({ kind: 'info', text: t('remote.copyByHand') });
    }
  }

  async function share() {
    if (!link) return;
    const text = t('remote.shareText', { property: props.inspection.property.address || t('inspection.noAddress') });
    try {
      if (Capacitor.isNativePlatform()) await Share.share({ title: t('remote.title'), text, url: link.url });
      else if (typeof navigator.share === 'function') await navigator.share({ title: t('remote.title'), text, url: link.url });
      else await copy();
    } catch {
      // the inspector closed the share sheet
    }
  }

  return (
    <SheetShell title={t('remote.title')} subtitle={t('remote.subtitle')} onClose={props.onClose} layer="over" testId="remote-sign-sheet">
      <ul className="billing-terms remote-steps" data-testid="remote-steps">
        <li>{t('remote.step1')}</li>
        <li>{t('remote.step2')}</li>
        <li>{t('remote.step3')}</li>
      </ul>
      {taken ? (
        <>
          <InlineMessage kind="ok" text={t('remote.taken')} />
          <Button icon="draw" onClick={props.onSignNow} testId="remote-sign-now">
            {t('remote.signNow')}
          </Button>
        </>
      ) : link ? (
        <div className="form-stack" data-testid="remote-link">
          <p className="remote-url" data-testid="remote-url">
            {link.url}
          </p>
          <p className="card-note" data-testid="remote-expires">
            {t('remote.validUntil', { date: formatDate(link.expiresAt) })}
          </p>
          <div className="btn-pair">
            <Button variant="secondary" icon="link" onClick={() => void copy()} testId="remote-copy">
              {t('remote.copy')}
            </Button>
            <Button variant="secondary" icon="ios_share" onClick={() => void share()} testId="remote-share">
              {t('remote.share')}
            </Button>
          </div>
          {message ? <InlineMessage kind={message.kind} text={message.text} /> : null}
          <Button icon="refresh" onClick={() => void check()} disabled={busy} testId="remote-check">
            {busy ? t('remote.checking') : t('remote.check')}
          </Button>
          <Button variant="text" icon="link_off" onClick={() => void discardRemoteSignAction(props.inspection.id)} disabled={busy} testId="remote-discard">
            {t('remote.discard')}
          </Button>
        </div>
      ) : (
        <div className="form-stack">
          <p className="card-note">{t('remote.changeNote')}</p>
          {!ready ? <InlineMessage kind="info" text={t('remote.notReady')} /> : null}
          {message ? <InlineMessage kind={message.kind} text={message.text} /> : null}
          <Button icon="link" onClick={() => void create()} disabled={busy || !ready} testId="remote-create">
            {busy ? t('remote.creating') : t('remote.create')}
          </Button>
        </div>
      )}
    </SheetShell>
  );
}
